import { Component, computed, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { LpWizardTargetDirective } from '@gametailors/leerplus';
import { delay, shuffle } from '@gametailors/web-core/misc';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidKlankSwitchKlank } from '../../components/rid-klank-switch-klank/rid-klank-switch-klank';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOption, TOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { PronunciationService } from '../../services/pronunciation.service';
import { AnnotationUtils, TAnnotatedKlankvoet } from '../../utils/annotation.utils';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { KlankGenerator } from './rid-klank-switch.utils';

/** Whether a word was spelled right the first time 'Klaar!' was pressed. */
export interface TKlankSwitchAnswer {
  index: number;
  correct: boolean;
}

export interface TKlankSwitchAssignment {
  prompt: TOption;
  word: TAnnotatedKlankvoet[];
  unit: 'KLANK' | 'KLANKVOET';
  /** How many tiles start out switched. */
  shuffle: number;
}

/** The tiles that spell the word, e.g. `['t', 'ij', 'g', 'e', 'r']` per klank or `['tij', 'ger']` per klankvoet. */
export function getKlankSwitchTiles({ word, unit }: Pick<TKlankSwitchAssignment, 'word' | 'unit'>): string[] {
  return unit === 'KLANKVOET'
    ? word.map((voet) => AnnotationUtils.write(voet))
    : word.flatMap((voet) => AnnotationUtils.writeKlanken(voet));
}

@Component({
  selector: 'rid-klank-switch',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidOption,
    RidBottomButton,
    RidKlankSwitchKlank,
    RidSfxDirective,
    LpWizardTargetDirective,
  ],
  templateUrl: './rid-klank-switch.html',
  host: {
    class: 'block',
  },
})
export class RidKlankSwitch implements OnInit {
  private readonly $pronunciationService = inject(PronunciationService);

  assignments = input.required<TKlankSwitchAssignment[]>();
  autoPresent = input(true);

  index = signal(0);
  assignment = computed(() => this.assignments()[this.index()]);
  answer = computed(() => getKlankSwitchTiles(this.assignment()));
  progress = signal(0);
  item = viewChild.required<RidOption>('item');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  text = signal<string[]>([]);
  isEvaluating = signal(false);
  showKlanken = signal(false);
  hasChanged = signal(false);
  /** Whether the klanken currently spell the answer (not yet confirmed with 'Klaar!'). */
  isWordCorrect = computed(() => this.answer().every((k, i) => k === this.text()[i]));

  private generators: KlankGenerator[] = [];
  private hasCheckedIncorrect = false;

  /** Emitted once a correct answer has been pronounced, before moving on to the next assignment (or finishing). */
  correct = output<void>();
  /** Emitted once per word, when it has been spelled right. */
  answered = output<TKlankSwitchAnswer>();
  finished = output<void>();

  ngOnInit(): void {
    this.initialize(this.assignment());
  }

  initialize(assignment: TKlankSwitchAssignment) {
    const answer = getKlankSwitchTiles(assignment);
    this.generators =
      assignment.unit === 'KLANKVOET'
        ? assignment.word.map((voet) => KlankGenerator.forKlankvoet(voet))
        : answer.map((_, i) => KlankGenerator.forKlank(answer, i));
    const indicesToChange = shuffle(Array.from({ length: answer.length }, (_, i) => i)).slice(0, assignment.shuffle);
    this.text.set(answer.map((k, i) => (indicesToChange.includes(i) ? this.generators[i].getNext() : k)));
    this.showKlanken.set(false);
    this.hasChanged.set(false);
    this.hasCheckedIncorrect = false;
  }

  change(index: number) {
    if (this.isEvaluating()) return;

    const k = this.generators[index].getNext();
    AudioEffectPlayer.play('POP');
    this.text.update((text) => {
      const newText = [...text];
      newText[index] = k;
      return newText;
    });
    this.hasChanged.set(true);
  }

  async onItemPoppedIn() {
    if (!this.autoPresent()) return;
    await this.present();
  }

  /** Plays the word and then shows the klanken. */
  async present() {
    await this.item().present();
    this.showKlanken.set(true);
  }

  check() {
    const overlay = this.overlayFeedback();
    if (this.isWordCorrect()) {
      this.markCorrect();
    } else {
      this.hasCheckedIncorrect = true;
      AudioEffectPlayer.play('INCORRECT');
      overlay.showIncorrect();
    }
  }

  async markCorrect() {
    this.isEvaluating.set(true);
    this.answered.emit({ index: this.index(), correct: !this.hasCheckedIncorrect });

    const isLast = this.index() === this.assignments().length - 1;

    if (isLast) {
      this.progress.update((p) => p + 1);
    }
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(250);
    await this.$pronunciationService.tryPronounce(this.answer().join(''));
    this.correct.emit();

    if (isLast) {
      this.finish();
    } else {
      this.progress.update((p) => p + 1);
      this.index.update((i) => i + 1);
      this.initialize(this.assignment());
      this.isEvaluating.set(false);
    }
  }

  finish() {
    this.finished.emit();
  }
}
